import { apiClient } from "../../../../lib/axios";

export type Participant = {
  id: string;
  user_id?: string | null;
  title?: string | null;
  first_name: string;
  surname: string;
  gender?: string | null;
  email?: string | null;
  contact_number?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  address_line_3?: string | null;
  town?: string | null;
  postcode?: string | null;
  company?: string | null;
  job_role?: string | null;
  status?: string | null;
  current_whoop_user?: boolean | null;
  whoop_received?: boolean | null;
  whoop_unite_email_sent?: boolean | null;
  whoop_unite_connected?: boolean | null;
  peak4_app?: boolean | null;
  peak4_api_connected?: boolean | null;
  date_started?: string | null;
  date_added_to_lms?: string | null;
  date_to_be_removed_from_lms?: string | null;
  color_tag?: string | null;
  flagged?: string | null;
  compliance_asset_id?: string | null;
  notes?: string | null;
  using_own_device?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  user_email?: string | null;
  user_first_name?: string | null;
  user_last_name?: string | null;
  wear_jeopardy_status?:
    | "not_connected"
    | "recently_connected"
    | "not_worn"
    | "intermittent_wear"
    | "wearing_consistently"
    | null;
  wear_jeopardy_status_reason?: string | null;
  wear_jeopardy_last_data_at?: string | null;
  first_connected_at?: string | null;
  needs_reauth?: boolean | null;
  signup_answers?: Array<{
    source: "pre_registration" | "onboarding";
    question_id: string;
    title: string;
    answer: string | null;
  }>;
};

export type ParticipantDevice = {
  id: string;
  name: string;
  type?: string | null;
  notes?: string | null;
};

export type ParticipantDetailResponse = {
  data: Participant & { devices: ParticipantDevice[] };
};

export type ParticipantListResponse = {
  data: Participant[];
};

export type ParticipantPayload = Partial<Participant> & {
  first_name: string;
  surname: string;
  device_ids?: string[];
};

export type ParticipantUser = {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  client_id?: string | null;
  client_name?: string | null;
};

export type Device = {
  id: string;
  name: string;
  type?: string | null;
  active?: boolean | null;
};

export type TaskTemplate = {
  id: string;
  name: string;
  description?: string | null;
  active?: boolean | null;
  sort_order?: number | null;
  applies_to?: string | null;
  category?: string | null;
};

export type ParticipantInteraction = {
  id: string;
  participant_id: string;
  occurred_at?: string | null;
  type?: string | null;
  summary?: string | null;
  body: string;
  created_by_user_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

export type ParticipantDocument = {
  id: string;
  participant_id: string;
  file_name: string;
  mime_type?: string | null;
  size_bytes?: number | null;
  storage_key: string;
  uploaded_by_user_id?: string | null;
  uploaded_at?: string | null;
  file_url?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

export type ParticipantTask = {
  id: string;
  participant_id: string;
  template_id?: string | null;
  title: string;
  description?: string | null;
  status?: string | null;
  due_date?: string | null;
  completed_at?: string | null;
  assigned_to_user_id?: string | null;
  sort_order?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  template_name?: string | null;
  assigned_first_name?: string | null;
  assigned_last_name?: string | null;
};

export type ParticipantWearJeopardyRow = {
  participant_id: string;
  user_id: string;
  participant_name: string;
  participant_email?: string | null;
  company?: string | null;
  integration_connected: boolean;
  last_data_at?: string | null;
  days_with_data_7d: number;
  days_with_data_14d: number;
  days_with_data_28d: number;
  wear_ratio_14d: number;
  longest_gap_days_28d: number;
  wear_status:
    | "not_connected"
    | "recently_connected"
    | "not_worn"
    | "intermittent_wear"
    | "wearing_consistently";
  wear_status_reason: string;
  latest_note?: string | null;
  latest_note_created_at?: string | null;
  latest_note_created_by?: string | null;
  latest_note_author_name?: string | null;
  note_count?: number | null;
};

export type ParticipantWearJeopardyListResponse = {
  data: {
    page: number;
    page_size: number;
    total: number;
    rows: ParticipantWearJeopardyRow[];
  };
};

export type SignupAnswerRow = {
  participant_id: string;
  source: "pre_registration" | "onboarding";
  question_id: string;
  title: string;
  answer: string | null;
};

export type SignupAnswersBulkResponse = {
  data: {
    answers: SignupAnswerRow[];
    questions: Array<{ id: string; title: string }>;
  };
};

export type ParticipantWearJeopardyNote = {
  id: string;
  participant_id: string;
  note: string;
  created_by?: string | null;
  created_at: string;
  first_name?: string | null;
  last_name?: string | null;
};

export const participantsApi = {
  listSignupAnswersBulk: async (params?: {
    client_id?: string;
  }): Promise<SignupAnswersBulkResponse> => {
    const response = await apiClient.get("/admin/participants/signup-answers", {
      params,
    });
    return response.data;
  },

  list: async (params?: {
    client_id?: string;
    search?: string;
    status?: string;
    flagged?: string;
    whoop_received?: boolean;
    using_own_device?: boolean;
  }): Promise<ParticipantListResponse> => {
    const response = await apiClient.get("/admin/participants", {
      params,
    });
    return response.data;
  },

  listUsers: async (clientId?: string | null): Promise<{ data: ParticipantUser[] }> => {
    const response = await apiClient.get("/admin/participants/users", {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  },

  listWearJeopardy: async (params?: {
    client_id?: string;
    search?: string;
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<ParticipantWearJeopardyListResponse> => {
    const response = await apiClient.get("/admin/participants/wear-jeopardy", {
      params,
    });
    return response.data;
  },

  listWearJeopardyNotes: async (
    participantId: string
  ): Promise<{ data: ParticipantWearJeopardyNote[] }> => {
    const response = await apiClient.get(
      `/admin/participants/${participantId}/wear-jeopardy-notes`
    );
    return response.data;
  },

  createWearJeopardyNote: async (
    participantId: string,
    payload: { note: string }
  ): Promise<{ data: ParticipantWearJeopardyNote }> => {
    const response = await apiClient.post(
      `/admin/participants/${participantId}/wear-jeopardy-notes`,
      payload
    );
    return response.data;
  },

  get: async (participantId: string): Promise<ParticipantDetailResponse> => {
    const response = await apiClient.get(`/admin/participants/${participantId}`);
    return response.data;
  },

  create: async (payload: ParticipantPayload): Promise<ParticipantDetailResponse> => {
    const response = await apiClient.post("/admin/participants", payload);
    return response.data;
  },

  update: async (
    participantId: string,
    payload: ParticipantPayload
  ): Promise<ParticipantDetailResponse> => {
    const response = await apiClient.put(
      `/admin/participants/${participantId}`,
      payload
    );
    return response.data;
  },

  remove: async (participantId: string): Promise<{ status: string }> => {
    const response = await apiClient.delete(`/admin/participants/${participantId}`);
    return response.data;
  },

  listDevices: async (active?: boolean): Promise<{ data: Device[] }> => {
    const response = await apiClient.get("/admin/devices", {
      params: typeof active === "boolean" ? { active } : undefined,
    });
    return response.data;
  },

  createDevice: async (payload: Partial<Device>): Promise<{ data: Device }> => {
    const response = await apiClient.post("/admin/devices", payload);
    return response.data;
  },

  updateDevice: async (
    deviceId: string,
    payload: Partial<Device>
  ): Promise<{ data: Device }> => {
    const response = await apiClient.put(`/admin/devices/${deviceId}`, payload);
    return response.data;
  },

  deleteDevice: async (deviceId: string): Promise<{ status: string }> => {
    const response = await apiClient.delete(`/admin/devices/${deviceId}`);
    return response.data;
  },

  listTaskTemplates: async (active?: boolean): Promise<{ data: TaskTemplate[] }> => {
    const response = await apiClient.get("/admin/task-templates", {
      params: typeof active === "boolean" ? { active } : undefined,
    });
    return response.data;
  },

  createTaskTemplate: async (
    payload: Partial<TaskTemplate>
  ): Promise<{ data: TaskTemplate }> => {
    const response = await apiClient.post("/admin/task-templates", payload);
    return response.data;
  },

  updateTaskTemplate: async (
    templateId: string,
    payload: Partial<TaskTemplate>
  ): Promise<{ data: TaskTemplate }> => {
    const response = await apiClient.put(
      `/admin/task-templates/${templateId}`,
      payload
    );
    return response.data;
  },

  deleteTaskTemplate: async (templateId: string): Promise<{ status: string }> => {
    const response = await apiClient.delete(
      `/admin/task-templates/${templateId}`
    );
    return response.data;
  },

  listInteractions: async (
    participantId: string
  ): Promise<{ data: ParticipantInteraction[] }> => {
    const response = await apiClient.get(
      `/admin/participants/${participantId}/interactions`
    );
    return response.data;
  },

  createInteraction: async (
    participantId: string,
    payload: Partial<ParticipantInteraction>
  ): Promise<{ data: ParticipantInteraction }> => {
    const response = await apiClient.post(
      `/admin/participants/${participantId}/interactions`,
      payload
    );
    return response.data;
  },

  updateInteraction: async (
    participantId: string,
    interactionId: string,
    payload: Partial<ParticipantInteraction>
  ): Promise<{ data: ParticipantInteraction }> => {
    const response = await apiClient.put(
      `/admin/participants/${participantId}/interactions/${interactionId}`,
      payload
    );
    return response.data;
  },

  deleteInteraction: async (
    participantId: string,
    interactionId: string
  ): Promise<{ status: string }> => {
    const response = await apiClient.delete(
      `/admin/participants/${participantId}/interactions/${interactionId}`
    );
    return response.data;
  },

  listDocuments: async (
    participantId: string
  ): Promise<{ data: ParticipantDocument[] }> => {
    const response = await apiClient.get(
      `/admin/participants/${participantId}/documents`
    );
    return response.data;
  },

  createDocumentUploadUrl: async (
    participantId: string,
    payload: { file_name: string; mime_type: string }
  ): Promise<{ upload_url: string; storage_key: string; file_url: string }> => {
    const response = await apiClient.post(
      `/admin/participants/${participantId}/documents/upload-url`,
      payload
    );
    return response.data;
  },

  createDocument: async (
    participantId: string,
    payload: {
      file_name: string;
      mime_type?: string | null;
      size_bytes?: number | null;
      storage_key: string;
    }
  ): Promise<{ data: ParticipantDocument }> => {
    const response = await apiClient.post(
      `/admin/participants/${participantId}/documents`,
      payload
    );
    return response.data;
  },

  deleteDocument: async (
    participantId: string,
    documentId: string
  ): Promise<{ status: string }> => {
    const response = await apiClient.delete(
      `/admin/participants/${participantId}/documents/${documentId}`
    );
    return response.data;
  },

  listTasks: async (
    participantId: string
  ): Promise<{ data: ParticipantTask[] }> => {
    const response = await apiClient.get(
      `/admin/participants/${participantId}/tasks`
    );
    return response.data;
  },

  createTask: async (
    participantId: string,
    payload: Partial<ParticipantTask>
  ): Promise<{ data: ParticipantTask }> => {
    const response = await apiClient.post(
      `/admin/participants/${participantId}/tasks`,
      payload
    );
    return response.data;
  },

  updateTask: async (
    participantId: string,
    taskId: string,
    payload: Partial<ParticipantTask>
  ): Promise<{ data: ParticipantTask }> => {
    const response = await apiClient.put(
      `/admin/participants/${participantId}/tasks/${taskId}`,
      payload
    );
    return response.data;
  },

  deleteTask: async (
    participantId: string,
    taskId: string
  ): Promise<{ status: string }> => {
    const response = await apiClient.delete(
      `/admin/participants/${participantId}/tasks/${taskId}`
    );
    return response.data;
  },

  applyDefaultTasks: async (
    participantId: string
  ): Promise<{ created: number }> => {
    const response = await apiClient.post(
      `/admin/participants/${participantId}/tasks/apply-defaults`
    );
    return response.data;
  },
};
